import { subscriptionCost } from "constants/subscriptions";

import { useState } from "react";

import { Alert, Typography, useMediaQuery, useTheme } from "@mui/material";
import { subscription } from "API/calls";
import { DialogSx } from "components/elements/Dialog";
import { useUserDataContext } from "context/UserDataContext";
import { useAPICreditsPaymentTypes } from "hooks/useAPI";
import useConfirm from "hooks/useConfirm";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { SubscriptionType } from "types/enums";
import { ISubscriptionResponse } from "types/types";

import { IDowngradingFailData } from "../ModalDowngradingFail/types";

export const useSubscription = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const { confirm } = useConfirm();
  const { subscriptionType: actualSubscription, refreshData, creditsRechargeType } = useUserDataContext();
  const { data: creditsPaymentTypesData } = useAPICreditsPaymentTypes(!!creditsRechargeType);

  const [modalNotEnoughCreditsOpen, setModalNotEnoughCreditsOpen] = useState<boolean>(false);
  const [modalDowngradingFailOpen, setModalDowngradingFailOpen] = useState<boolean>(false);
  const [subscriptionResponse, setSubscriptionResponse] = useState<IDowngradingFailData>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const changeSubscription = async (subscriptionType: SubscriptionType) => {
    try {
      if (actualSubscription === SubscriptionType.PRO) {
        await confirm({
          title: t("subscriptions.proDowngradeModal.title"),
          description: (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("subscriptions.proDowngradeModal.alert")}
              </Alert>
              <Typography color="text.primary" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                <Trans i18nKey="subscriptions.proDowngradeModal.description" />
              </Typography>
            </>
          ),
          confirmationText: t("subscriptions.proDowngradeModal.confirmButton"),
          allowClose: true,
          dialogProps: { sx: { ...DialogSx({ fullWidth: true, isMobile }) } },
        });
      }

      processSubscriptionChange(subscriptionType);
    } catch (err: any) {
      // silent
    }
  };

  function processSubscriptionChange(subscriptionType: SubscriptionType) {
    setSubscriptionResponse(undefined);
    setIsLoading(true);

    subscription(subscriptionType)
      .then((response) => {
        const subscriptionResponse: ISubscriptionResponse = response?.data;
        if (subscriptionResponse.ok) {
          enqueueSnackbar(t("activated"), { variant: "success" });
          refreshData && refreshData();
          if (creditsRechargeType) {
            const paymentCreditsAmount = creditsPaymentTypesData?.creditsPaymentTypes?.[creditsRechargeType]?.creditsAmount ?? 0;
            const newSubscriptionCost = subscriptionCost[subscriptionType];

            if (paymentCreditsAmount !== newSubscriptionCost) {
              showCreditsRechargeTypeActiveModal(paymentCreditsAmount);
            }
          }
        } else {
          setModalDowngradingFailOpen(true);
          setSubscriptionResponse({ ...subscriptionResponse, subscriptionType });
        }
      })
      .catch((error) => {
        if (error?.response?.data?.httpStatus === "FORBIDDEN") {
          setModalNotEnoughCreditsOpen(true);
        } else {
          enqueueSnackbar(t("notActivated"), { variant: "error" });
        }
      });
  }

  const showCreditsRechargeTypeActiveModal = async (paymentCreditsAmount: number) => {
    try {
      await confirm({
        title: t("subscriptions.creditsRechargeTypeActiveModal.title"),
        description: t("subscriptions.creditsRechargeTypeActiveModal.description", { amount: paymentCreditsAmount }),
        dialogProps: { maxWidth: "md", sx: { ...DialogSx({ fullWidth: true, isMobile }) } },
        confirmationText: t("subscriptions.creditsRechargeTypeActiveModal.button"),
        cancellationButtonProps: { sx: { display: "none" } },
      });
    } catch (err: any) {
      // silent
    }
  };

  const onModalDowngradingFailClose = () => {
    setModalDowngradingFailOpen(false);
    if(isLoading) setIsLoading(false);
  }

  const onModalNotEnoughCredits = () => {
    setModalNotEnoughCreditsOpen(false);
    if(isLoading) setIsLoading(false);
  }

  return {
    actualSubscription,
    isLoading,
    modalNotEnoughCreditsOpen,
    modalDowngradingFailOpen,
    subscriptionResponse,
    onModalDowngradingFailClose,
    onModalNotEnoughCredits,
    changeSubscription,
    setIsLoading,
    creditsRechargeType,
  };
};
